import md5 from "blueimp-md5";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

export const hash = (object) => md5(JSON.stringify(object));

const PREFIX = "__use_local_storage_state_hook";

const getItem = (key) => {
    try {
        const value = window.localStorage.getItem(key);
        if (value && value !== "undefined") {
            return JSON.parse(value);
        }
    } catch (error) {
        console.error("error in useLocalStorageState: ", error);
    }

    return undefined;
};

const setItem = (key, value) => {
    window.localStorage.setItem(key, JSON.stringify(value));
};

export const usePersistentState = <T>(keyName: string, initialValue?: unknown) => {
    const hashKey = `${PREFIX}__initial_value_hash__${keyName}`;
    const valueKey = `${PREFIX}__value__${keyName}`;
    // First client render must match SSR. Reading localStorage here used to expand
    // sidebar accordions before hydration while the server rendered them closed.
    const [hasSetState, setHasSetState] = useState(false);
    const [state, setState] = useState<T>(() =>
        initialValue instanceof Function ? initialValue() : initialValue as T
    );

    const initialValueHash =
        initialValue === undefined ? undefined : hash(initialValue);
    
    useEffect(() => {
        setState(typeof window !== "undefined" ? getItem(valueKey) :
            (initialValue instanceof Function ? initialValue() : initialValue));
    }, [initialValueHash, initialValue]);

    useEffect(() => {
        setItem(hashKey, initialValueHash);
    }, [hashKey, initialValueHash]);

    useEffect(() => {
        if (hasSetState) {
            setItem(valueKey, state);
        } else {
            // `??` not `||`: a stored `false` or `0` is a choice, not a missing value.
            setState(
                () =>
                    getItem(valueKey) ??
                    (initialValue instanceof Function ? initialValue() : initialValue)
            );
            setHasSetState(true);
        }
    }, [keyName, hasSetState, initialValue, state]);

    return [state, setState] as [T, Dispatch<SetStateAction<T>>];
};

export default usePersistentState;
